import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Typography, Tag, Card, Steps, Descriptions, Alert, Button, Flex, Row, Col, Spin, Timeline, Collapse, Image, App, theme, Tooltip, Popconfirm, Switch, Modal, Checkbox,
} from "antd";
import { ArrowLeftOutlined, StopOutlined, DownloadOutlined, FileTextOutlined, RedoOutlined, LaptopOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import { supabase } from "@/configs/supabase/config";
import { useQ_PageSessions_Detail, type PageSessions_Detail_QueryData } from "@/hooks/Sessions/useQ_PageSessions_Detail";
import { useM_PageSessions_SessionRerun } from "@/hooks/Sessions/useM_PageSessions_SessionRerun";
import type { SessionComposerValues } from "@/components/App_SessionComposer/App_SessionComposer";
import { useQ_PageSessions_AuditLog } from "@/hooks/Sessions/useQ_PageSessions_AuditLog";
import { useQ_PageSessions_Snapshots, type PageSessions_Snapshots_QueryData } from "@/hooks/Sessions/useQ_PageSessions_Snapshots";
import { deriveSessionStatus } from "@/utils/utils_Sessions_DerivedStatus";

dayjs.extend(relativeTime);
dayjs.extend(duration);

const RATE_CHECK_STEPS = ["verify-auth", "A2-qualification", "A3-quote-form", "A5-middle-pages", "A6-payment-check"];
const GENERATE_POLICY_STEPS = [...RATE_CHECK_STEPS, "B1-purchase", "B2-post-purchase"];

function fmtDur(ms: number): string {
  const d = dayjs.duration(ms);
  const mins = Math.floor(d.asMinutes());
  const secs = d.seconds();
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

export const Route = createFileRoute("/_protected/$organizationId/$flowId/sessions/$sessionId")({
  component: Page_SessionDetail,
});

function Page_SessionDetail() {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { organizationId, flowId, sessionId } = Route.useParams();
  const navigate = useNavigate();
  const qDetail = useQ_PageSessions_Detail({ sessionId });
  const mSessionRerun = useM_PageSessions_SessionRerun({ organizationId, flowSlug: flowId });
  const [stopping, setStopping] = useState(false);
  const [localBrowser, setLocalBrowser] = useState(() => localStorage.getItem("ihub:localBrowser") === "true");
  const [realRerunOpen, setRealRerunOpen] = useState(false);
  const [realRerunAck, setRealRerunAck] = useState(false);

  const handleStop = async () => {
    setStopping(true);
    const { error } = await supabase.from("sessions").update({
      is_alive: "stopped" as never,
      end_code: "MANUAL_STOP",
      end_category: "lifecycle",
      end_message: "Stopped by operator",
      ended_at: new Date().toISOString(),
    }).eq("id", sessionId);
    setStopping(false);
    if (error) { message.error("Failed to stop"); return; }
    message.success("Session stopped");
    qDetail.query.refetch();
  };

  if (qDetail.query.isLoading) {
    return <Flex justify="center" style={{ padding: token.paddingXL }}><Spin size="large" /></Flex>;
  }

  if (!qDetail.session) {
    return <Alert type="error" message="Session not found" />;
  }

  const session = qDetail.session;
  const canStop = session.is_alive !== "stopped";
  const canRerun = !!(session.first_name && session.last_name && session.date_of_birth && session.drivers_license && session.rate_state);
  const rerunMode = session.mode === "generate-policy" ? "generate-policy" : "rate-check";
  // Only generate-policy actually spends money — a real rate-check just drops
  // the test label. Gate the acknowledgement on the case that binds.
  const willBind = rerunMode === "generate-policy";

  const handleRerun = async (sessionType: "test" | "real" = "test") => {
    const values: SessionComposerValues = {
      customerData: {
        firstName: session.first_name ?? "",
        lastName: session.last_name ?? "",
        dob: session.date_of_birth ?? "",
        driversLicense: session.drivers_license ?? "",
        phone: session.phone ?? "",
        email: session.email ?? "",
        ...(session.license_state ? { licenseState: session.license_state } : {}),
        ...(session.gender ? { gender: session.gender } : {}),
        ...(session.marital_status ? { maritalStatus: session.marital_status } : {}),
        address: {
          street: session.address_street ?? "",
          city: session.address_city ?? "",
          state: session.address_state ?? "",
          zip: session.address_zip ?? "",
        },
      },
      quoteDetails: {
        state: session.rate_state ?? "",
        policyType: session.policy_type ?? "",
      },
      mode: rerunMode,
    };
    // A real run never goes to the local browser: local Chrome shares the
    // carrier login and replaying production traffic through it risks the
    // shared Dairyland account. Cloud Run only.
    const useLocal = sessionType === "test" && import.meta.env.DEV && localBrowser;
    try {
      const res = await mSessionRerun.mutation.mutateAsync({
        values, sessionType, ...(useLocal ? { localBrowser: true } : {}),
      });
      message.success(sessionType === "real" ? "Real rerun started" : "Test rerun started");
      setRealRerunOpen(false);
      setRealRerunAck(false);
      navigate({ to: "/$organizationId/$flowId/sessions/$sessionId", params: { organizationId, flowId, sessionId: res.sessionId } });
    } catch {
      message.error("Failed to start rerun");
    }
  };

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: token.marginMD }}>
        <Flex align="center" gap={token.marginSM}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            size="small"
            onClick={() => navigate({ to: "/$organizationId/$flowId/sessions", params: { organizationId, flowId } })}
          />
          <Typography.Title level={4} style={{ margin: 0 }}>
            Session Detail
          </Typography.Title>
        </Flex>
        <Flex gap={token.marginXS}>
          <Tooltip title={canRerun ? undefined : "Cannot rerun: session is missing required input fields"}>
            <Popconfirm
              title="Rerun as test session?"
              description={
                <Flex vertical gap={token.marginXS} style={{ maxWidth: 320 }}>
                  <Typography.Text>
                    Starts a new {rerunMode} test session with this session's input. No carrier bind, no charge.
                  </Typography.Text>
                  {import.meta.env.DEV && (
                    <Flex align="center" gap={token.marginSM}>
                      <Switch
                        checked={localBrowser}
                        onChange={(checked) => {
                          setLocalBrowser(checked);
                          localStorage.setItem("ihub:localBrowser", String(checked));
                        }}
                        checkedChildren={<LaptopOutlined />}
                        unCheckedChildren={<LaptopOutlined />}
                        size="small"
                      />
                      <Typography.Text type={localBrowser ? "warning" : "secondary"} style={{ fontSize: token.fontSizeSM }}>
                        {localBrowser ? "Local browser" : "Cloud Run"}
                      </Typography.Text>
                    </Flex>
                  )}
                </Flex>
              }
              okText="Rerun"
              onConfirm={() => handleRerun("test")}
              disabled={!canRerun}
            >
              <Button icon={<RedoOutlined />} loading={mSessionRerun.mutation.isPending} disabled={!canRerun}>
                Rerun as test
              </Button>
            </Popconfirm>
          </Tooltip>
          <Tooltip title={canRerun ? undefined : "Cannot rerun: session is missing required input fields"}>
            <Button
              danger
              icon={<WarningOutlined />}
              disabled={!canRerun}
              onClick={() => { setRealRerunAck(false); setRealRerunOpen(true); }}
            >
              Rerun as real
            </Button>
          </Tooltip>
          {canStop && (
            <Button icon={<StopOutlined />} onClick={handleStop} loading={stopping}>
              Stop
            </Button>
          )}
        </Flex>
      </Flex>

      <Modal
        open={realRerunOpen}
        title="Rerun as a real session?"
        okText={willBind ? "Bind real policy" : "Run for real"}
        okButtonProps={{ danger: true, disabled: willBind && !realRerunAck }}
        confirmLoading={mSessionRerun.mutation.isPending}
        onOk={() => handleRerun("real")}
        onCancel={() => setRealRerunOpen(false)}
        destroyOnHidden
      >
        <Flex vertical gap={token.marginSM}>
          {willBind ? (
            <Alert
              type="error"
              showIcon
              message="This binds a real policy"
              description="A new generate-policy session runs against the carrier with no test guard: it purchases a real policy and charges the agency. This cannot be undone from the hub."
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message="Rate-check only — nothing is bound"
              description="This session's mode is rate-check, so a real run quotes the carrier without purchasing. It is recorded as a real session and will trigger failure notifications."
            />
          )}

          {session.policy_number && (
            <Alert
              type="warning"
              showIcon
              message={`This session already bound policy ${session.policy_number}`}
              description="Rerunning as real will create a SECOND policy for the same customer. Only continue if you have confirmed the first one is void."
            />
          )}

          <Descriptions size="small" column={1} bordered
            items={[
              { key: "customer", label: "Customer", children: `${session.first_name ?? ""} ${session.last_name ?? ""}`.trim() || "—" },
              { key: "state", label: "State", children: session.rate_state ?? "—" },
              { key: "mode", label: "Mode", children: rerunMode },
            ]}
          />

          {willBind && (
            <Checkbox checked={realRerunAck} onChange={(e) => setRealRerunAck(e.target.checked)}>
              <Typography.Text>
                I understand this purchases a real policy and charges the agency.
              </Typography.Text>
            </Checkbox>
          )}
        </Flex>
      </Modal>

      <SessionHeader session={session} />

      <div style={{ marginTop: token.marginLG }}>
        <SessionInput session={session} />
      </div>

      <div style={{ marginTop: token.marginLG }}>
        <StepTimeline session={session} />
      </div>

      <div style={{ marginTop: token.marginLG }}>
        <DataCards session={session} />
      </div>

      <PolicyDocuments sessionId={sessionId} organizationId={organizationId} />

      <div style={{ marginTop: token.marginLG }}>
        <AuditLogSection sessionId={sessionId} />
      </div>

      <div style={{ marginTop: token.marginLG }}>
        <StepGallery sessionId={sessionId} organizationId={organizationId} />
      </div>
    </div>
  );
}

function SessionHeader({ session }: { session: PageSessions_Detail_QueryData }) {
  const status = deriveSessionStatus(session);

  const timestamps = [
    { label: "Created", value: session.created_at },
    { label: "Last Activity", value: session.last_activity_at },
    { label: "Rated", value: session.rated_at },
    { label: "Purchased", value: session.purchased_at },
    { label: "Ended", value: session.ended_at },
  ].filter((t) => t.value != null);

  return (
    <Card size="small">
      <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
        <Descriptions.Item label="ID">
          <Typography.Text copyable={{ text: session.id }} style={{ fontFamily: "monospace" }}>
            {session.id.slice(0, 16)}...
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={status.color}>{status.label}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Provider">
          <Tag>{session.provider}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Mode">{session.mode ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="External Ref">{session.external_ref ?? "—"}</Descriptions.Item>
        {session.execution_ms != null && (
          <Descriptions.Item label="Runner">
            <Tooltip title="Runner execution time">
              <Tag color="green">exec {fmtDur(session.execution_ms)}</Tag>
            </Tooltip>
          </Descriptions.Item>
        )}
        {timestamps.map((t) => (
          <Descriptions.Item key={t.label} label={t.label}>
            {dayjs(t.value).format("MMM D, HH:mm:ss")}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </Card>
  );
}


function SessionInput({ session }: { session: PageSessions_Detail_QueryData }) {
  const { token } = theme.useToken();

  const fields = [
    { label: "Name", value: [session.first_name, session.last_name].filter(Boolean).join(" ") },
    { label: "DOB", value: session.date_of_birth },
    { label: "Phone", value: session.phone, warn: !session.phone },
    { label: "Email", value: session.email, warn: !session.email },
    { label: "DL", value: session.drivers_license },
    { label: "License State", value: session.license_state },
    { label: "Gender", value: session.gender },
    { label: "Marital Status", value: session.marital_status },
    { label: "Address", value: [session.address_street, session.address_city, session.address_state, session.address_zip].filter(Boolean).join(", ") },
    { label: "Rate State", value: session.rate_state },
    { label: "Policy Type", value: session.policy_type },
  ];

  return (
    <Collapse
      items={[{
        key: "input",
        label: (
          <Flex align="center" gap={token.marginXS}>
            <span>Session Input</span>
            {fields.some((f) => f.warn) && <Tag color="warning">Missing fields</Tag>}
          </Flex>
        ),
        children: (
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
            {fields.map((f) => (
              <Descriptions.Item key={f.label} label={f.label}>
                {f.value ? (
                  <Typography.Text>{f.value}</Typography.Text>
                ) : (
                  <Typography.Text type="danger">(empty)</Typography.Text>
                )}
              </Descriptions.Item>
            ))}
          </Descriptions>
        ),
      }]}
      defaultActiveKey={fields.some((f) => f.warn) ? ["input"] : []}
    />
  );
}

function StepTimeline({ session }: { session: PageSessions_Detail_QueryData }) {
  const completed = new Set(session.steps_completed ?? []);
  const steps = session.mode === "generate-policy" ? GENERATE_POLICY_STEPS : RATE_CHECK_STEPS;

  const lastCompleted = (session.steps_completed ?? []).at(-1);
  const items = steps.map((step) => {
    let status: "finish" | "process" | "wait" = "wait";
    if (completed.has(step)) status = "finish";
    else if (session.is_alive !== "stopped" && lastCompleted && steps.indexOf(step) === steps.indexOf(lastCompleted) + 1) status = "process";
    return { title: step, status };
  });

  return (
    <Card title="Progress" size="small">
      <Steps items={items} size="small" />
    </Card>
  );
}

function DataCards({ session }: { session: PageSessions_Detail_QueryData }) {
  const { token } = theme.useToken();
  const status = deriveSessionStatus(session);
  const showRate = (["rated", "paying", "purchased"] as string[]).includes(session.stage);
  const showPolicy = session.stage === "purchased";
  const showError = session.outcome === "early_termination" || session.outcome === "failed";
  const showOutstanding = session.outstanding_balance != null;

  if (!showRate && !showPolicy && !showError && !showOutstanding) return null;

  return (
    <Row gutter={[token.marginMD, token.marginMD]}>
      {showRate && session.rate_result && (
        <Col xs={24} lg={12}>
          <Card title="Rate Result" size="small">
            <Descriptions column={1} size="small">
              {Object.entries(session.rate_result as Record<string, unknown>).map(([key, value]) => (
                <Descriptions.Item key={key} label={key}>
                  {String(value ?? "—")}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Card>
        </Col>
      )}

      {showOutstanding && (
        <Col xs={24} lg={12}>
          <Card title="Outstanding Balance" size="small">
            <Typography.Paragraph type="secondary" style={{ marginBottom: token.marginSM }}>
              The carrier requires this unpaid balance from a prior policy to be paid with the new
              policy, and adds it to the down payment it collects. The tenant charges it on top of
              the quoted down payment.
            </Typography.Paragraph>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Balance">
                <Typography.Text strong type="warning">${session.outstanding_balance}</Typography.Text>
              </Descriptions.Item>
              {(() => {
                const detail = (session.outstanding_detail ?? {}) as {
                  amountDueNow?: number | null;
                  items?: { account?: string; policy?: string; amount?: number }[];
                };
                return (
                  <>
                    <Descriptions.Item label="Carrier amount due now">
                      {detail.amountDueNow != null ? `$${detail.amountDueNow}` : "—"}
                    </Descriptions.Item>
                    {(detail.items ?? []).map((item, i) => (
                      <Descriptions.Item key={`${item.account ?? i}-${i}`} label={`Prior policy ${i + 1}`}>
                        <Typography.Text style={{ fontFamily: "monospace", fontSize: token.fontSizeSM }}>
                          {[item.account, item.policy].filter(Boolean).join(" / ") || "—"}
                        </Typography.Text>
                        {item.amount != null && <Typography.Text>{` — $${item.amount}`}</Typography.Text>}
                      </Descriptions.Item>
                    ))}
                  </>
                );
              })()}
            </Descriptions>
          </Card>
        </Col>
      )}

      {showPolicy && (
        <Col xs={24} lg={12}>
          <Card title="Policy" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Policy Number">
                <Typography.Text strong>{session.policy_number ?? "—"}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Premium">{session.premium ? `$${session.premium}` : "—"}</Descriptions.Item>
              <Descriptions.Item label="Term">{session.term_months ? `${session.term_months} months` : "—"}</Descriptions.Item>
              <Descriptions.Item label="Effective Date">{session.effective_date ?? "—"}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      )}

      {showError && (
        <Col xs={24}>
          <Alert
            type={session.outcome === "early_termination" ? "warning" : "error"}
            message={
              <Flex gap={8} align="center">
                <Tag color={status.color}>{status.label}</Tag>
                <Tag>{session.end_code}</Tag>
                <Typography.Text type="secondary">{session.outcome}</Typography.Text>
              </Flex>
            }
            description={
              <Typography.Text copyable={{ text: session.end_message ?? "" }}>
                {session.end_message}
              </Typography.Text>
            }
          />
        </Col>
      )}
    </Row>
  );
}

function PolicyDocuments({ sessionId, organizationId }: { sessionId: string; organizationId: string }) {
  const { token } = theme.useToken();
  const qSnapshots = useQ_PageSessions_Snapshots({ sessionId });
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const pdfSnapshots = useMemo(
    () => qSnapshots.snapshots.filter((s) => s.content_type?.startsWith("application/pdf") && s.r2_key),
    [qSnapshots.snapshots],
  );

  useEffect(() => {
    if (pdfSnapshots.length === 0) return;
    setLoading(true);

    Promise.all(
      pdfSnapshots.map(async (snap) => {
        const res = await supabase.functions.invoke("files_r2_sign-read-url", {
          body: { r2Key: snap.r2_key, organizationId },
        });
        if (res.error) return null;
        const data = res.data as { readUrl: string };
        return { id: snap.id, url: data.readUrl };
      }),
    ).then((results) => {
      const urls: Record<string, string> = {};
      for (const r of results) {
        if (r) urls[r.id] = r.url;
      }
      setSignedUrls(urls);
      setLoading(false);
    });
  }, [pdfSnapshots, organizationId]);

  if (pdfSnapshots.length === 0) return null;

  return (
    <div style={{ marginTop: token.marginLG }}>
      <Card title="Policy Documents" size="small" loading={loading && Object.keys(signedUrls).length === 0}>
        <Collapse
          items={pdfSnapshots.map((snap) => {
            const url = signedUrls[snap.id];
            const filename = snap.r2_key?.split("/").pop() ?? "document.pdf";

            return {
              key: snap.id,
              label: (
                <Flex align="center" gap={token.marginSM}>
                  <FileTextOutlined />
                  <Typography.Text>{filename}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {snap.image_bytes ? `${(snap.image_bytes / 1024).toFixed(0)} KB` : ""}
                  </Typography.Text>
                  {url && (
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={(e) => { e.stopPropagation(); window.open(url, "_blank"); }}
                    >
                      Download
                    </Button>
                  )}
                </Flex>
              ),
              children: url ? (
                <iframe
                  src={url}
                  title={filename}
                  style={{ width: "100%", height: 600, border: "none", borderRadius: token.borderRadiusSM }}
                />
              ) : (
                <Spin />
              ),
            };
          })}
        />
      </Card>
    </div>
  );
}

const EVENT_DOT_COLOR: Record<string, string> = {
  "session.created": "blue",
  "session.step_completed": "green",
  "session.state_changed": "gray",
  "session.failed": "red",
  "session.expired": "red",
};

function AuditLogSection({ sessionId }: { sessionId: string }) {
  const { token } = theme.useToken();
  const qAudit = useQ_PageSessions_AuditLog({ sessionId });

  if (qAudit.query.isLoading) return <Card size="small" loading />;

  if (qAudit.events.length === 0) {
    return (
      <Card title="Audit Log" size="small">
        <Typography.Text type="secondary">No audit events</Typography.Text>
      </Card>
    );
  }

  const timelineItems = qAudit.events.map((event) => ({
    color: EVENT_DOT_COLOR[event.event_type] ?? "gray",
    children: (
      <div key={event.id}>
        <Flex gap={token.marginXS} align="center" wrap="wrap">
          <Tag>{event.event_type}</Tag>
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {dayjs(event.created_at).format("HH:mm:ss")}
          </Typography.Text>
        </Flex>
        {event.detail && (
          <div style={{ marginTop: 4 }}>
            {Object.entries(event.detail as Record<string, unknown>).map(([k, v]) => (
              <div key={k}>
                <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>{k}: </Typography.Text>
                <Typography.Text style={{ fontSize: token.fontSizeSM }}>{String(v ?? "")}</Typography.Text>
              </div>
            ))}
          </div>
        )}
      </div>
    ),
  }));

  return (
    <Collapse
      items={[{
        key: "audit",
        label: `Audit Log (${qAudit.events.length} events)`,
        children: <Timeline items={timelineItems} />,
      }]}
      defaultActiveKey={[]}
    />
  );
}

interface StepCard {
  stepName: string;
  imageUrl?: string;
  videoUrl?: string;
  isError: boolean;
  capturedAt: string;
  videoSize?: number;
  isVideo?: boolean;
  sequence?: number | null;
}

type SnapshotRow = PageSessions_Snapshots_QueryData[number];

/**
 * An artifact's purpose is the `kind` column. Rows written before that column
 * existed are classified by the naming conventions it replaced, so historical
 * sessions keep rendering.
 */
function resolveKind(snap: SnapshotRow): string {
  if (snap.kind) return snap.kind;
  if (snap.step_name.startsWith("error-")) return "error";
  if (snap.step_name.startsWith("debug-")) return "debug";
  if (snap.step_name.startsWith("full-run-")) return "video";
  if (snap.content_type === "application/pdf") return "document";
  return "step";
}

/** Strip a legacy purpose prefix so old and new rows land on the same card. */
const baseStepName = (stepName: string): string =>
  stepName.replace(/^(error|debug)-/, "");

function StepGallery({ sessionId, organizationId }: { sessionId: string; organizationId: string }) {
  const { token } = theme.useToken();
  const qSnapshots = useQ_PageSessions_Snapshots({ sessionId });
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [urlsLoading, setUrlsLoading] = useState(false);

  useEffect(() => {
    // Debug snapshots are never rendered — don't spend a sign-url round-trip on them.
    const snapsWithR2 = qSnapshots.snapshots.filter((s) => s.r2_key && resolveKind(s) !== "debug");
    if (snapsWithR2.length === 0) return;

    setUrlsLoading(true);

    Promise.all(
      snapsWithR2.map(async (snap) => {
        const { data, error } = await supabase.functions.invoke("files_r2_sign-read-url", {
          body: { r2Key: snap.r2_key, organizationId },
        });
        if (error) return null;
        const isVideo = snap.content_type?.startsWith("video/");
        const key = isVideo ? `video:${snap.step_name}` : snap.id;
        return { key, url: (data as { readUrl: string }).readUrl };
      }),
    ).then((results) => {
      const urls: Record<string, string> = {};
      for (const r of results) {
        if (r) urls[r.key] = r.url;
      }
      setSignedUrls(urls);
      setUrlsLoading(false);
    });
  }, [qSnapshots.snapshots, organizationId]);

  const stepCards = useMemo(() => {
    const map = new Map<string, StepCard>();

    for (const snap of qSnapshots.snapshots) {
      const kind = resolveKind(snap);
      // Debug snapshots are a diagnostic payload, not something to look at;
      // documents are rendered by PolicyDocuments above.
      if (kind === "debug" || kind === "document") continue;

      const baseStep = baseStepName(snap.step_name);
      const existing = map.get(baseStep) ?? { stepName: baseStep, isError: false, capturedAt: snap.captured_at };

      if (kind === "step") existing.imageUrl = signedUrls[snap.id] ?? "";
      // The error shot shares its step's card and flags it — but never replaces
      // the successful screenshot if somehow both exist.
      if (kind === "error") {
        existing.isError = true;
        existing.imageUrl = existing.imageUrl || signedUrls[snap.id] || "";
      }
      if (kind === "video") {
        existing.isVideo = true;
        existing.sequence = snap.sequence;
        existing.videoUrl = signedUrls[`video:${baseStep}`] ?? "";
        existing.videoSize = snap.image_bytes ?? undefined;
      }
      if (!existing.capturedAt || snap.captured_at < existing.capturedAt) existing.capturedAt = snap.captured_at;

      map.set(baseStep, existing);
    }

    // Videos play in the order their tabs were opened. Returning 0 for every
    // other pair leaves the rest untouched — Array.sort is stable.
    return Array.from(map.values()).sort((a, b) =>
      a.isVideo && b.isVideo ? (a.sequence ?? 0) - (b.sequence ?? 0) : 0,
    );
  }, [qSnapshots.snapshots, signedUrls]);

  if (qSnapshots.query.isLoading) return <Card size="small" loading />;

  if (stepCards.length === 0) {
    return (
      <Card title="Step Gallery" size="small">
        <Typography.Text type="secondary">No snapshots or recordings captured</Typography.Text>
      </Card>
    );
  }

  return (
    <Card title="Step Gallery" size="small">
      <Row gutter={[token.marginSM, token.marginSM]}>
        {stepCards.map((card) => (
          <Col key={card.stepName} xs={12} sm={8} md={6}>
            <div style={{ textAlign: "center" }}>
              {urlsLoading && !card.imageUrl && !card.videoUrl ? (
                <div style={{ width: 200, height: 120, background: token.colorBgContainerDisabled, borderRadius: token.borderRadiusSM, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Spin size="small" />
                </div>
              ) : card.videoUrl ? (
                <video
                  controls
                  preload="auto"
                  src={card.videoUrl}
                  poster={card.imageUrl || undefined}
                  style={{
                    width: 200,
                    borderRadius: token.borderRadiusSM,
                    border: card.isError ? `2px solid ${token.colorError}` : undefined,
                    background: token.colorBgContainerDisabled,
                  }}
                />
              ) : card.imageUrl ? (
                <Image
                  src={card.imageUrl}
                  width={200}
                  style={{
                    borderRadius: token.borderRadiusSM,
                    border: card.isError ? `2px solid ${token.colorError}` : undefined,
                  }}
                />
              ) : null}
              <div style={{ marginTop: 4 }}>
                {card.isError && <Tag color="error" style={{ fontSize: token.fontSizeSM }}>Error</Tag>}
                <Tag style={{ fontSize: token.fontSizeSM }}>{card.stepName}</Tag>
                <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {dayjs(card.capturedAt).format("HH:mm:ss")}
                </Typography.Text>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </Card>
  );
}
